'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { AskForPriceDialog } from '@eliadmin/core/src/components/product/dialog/ask-for-price-dialog';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { ProductStock } from '@eliadmin/core/src/components/product/product-stock';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent, CardFooter } from '@eliadmin/core/src/components/ui/card';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React, { useState } from 'react';
import { Fragment } from 'react';
import { buildImageSizes } from '@eliadmin/core/src/components/image/image-helpers';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);

  const priceDisplay = getProductPriceDisplay(product);
  const ASK_FOR_PRICE = priceDisplay.priceOnRequest;
  const showDiscount = priceDisplay.showPreviousPrice;
  const [isOpenAskForPriceDialog, setIsOpenAskForPriceDialog] = useState(false);

  return (
    <Card
      className={cn(
        // shadow-[0_-2px_60px_-15px_rgba(0,0,0,0.15)] removed, kept it there because of nice regex pattern
        'relative flex h-full min-w-[100px] flex-col gap-2 border border-border',
        'ea-product-card',
        className?.main,
        '@container',
      )}
    >
      {/* {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
        <div className={cn('absolute right-4 top-4 z-10', 'ea-product-card__add-to-favorites')}>
          <AddToFavoritesButton slug={product.slug} isFavorite={product.is_favorite ?? false} />
        </div>
      )} */}
      {!!templateSettings.product_teaser?.availability?.enabled && (
        <ProductStock
          className={cn(
            'absolute left-3 whitespace-nowrap',
            'ea-product-cart__stock',
            showDiscount ? 'top-10' : 'top-4',
          )}
        />
      )}
      {showDiscount ? (
        <div
          className={cn(
            'absolute left-3 top-4 flex w-full flex-row items-center justify-between',
            'ea-product-price__discount-wrapper',
          )}
        >
          <Badge
            size="md"
            className={cn(
              'pointer-events-none flex items-center gap-1 rounded-md bg-destructive font-semibold text-destructive-foreground hover:bg-destructive',
              'ea-product-price__discount',
              'top-4',
            )}
          >
            {React.cloneElement(PRODUCT_CONFIG.productPrice.discountIcon as any, {
              className: cn('ea-product-price__discount-icon'),
            })}
            {`${t('text24')} -${priceDisplay.discountLabel}%`}
          </Badge>
        </div>
      ) : (
        <></>
      )}
      <div
        className={cn(
          'absolute top-4 z-[9] flex flex-col flex-wrap gap-1 text-xs @[250px]:left-0 @[250px]:right-4 @[250px]:w-auto @[250px]:items-end @[250px]:pt-0',
          showDiscount &&
            templateSettings.product_teaser?.availability?.enabled &&
            'top-10 @[250px]:top-4',
          'left-3 w-full items-start pt-6',
          'ea-product-card__badges',
        )}
      >
        {product.tags
          ?.slice()
          .sort((a, b) => b.name.length - a.name.length)
          .map((badge, i) => (
            <Fragment key={`badge-${i}`}>
              <Badge
                style={{ backgroundColor: badge.color, color: getContrastYIQ(badge.color) }}
                size="sm"
                className={cn('ea-product-card__badge')}
              >
                {badge.name}
              </Badge>
            </Fragment>
          ))}
      </div>

      <CardContent
        className={cn(
          'flex flex-1 flex-col gap-2 p-1 pb-0 @[200px]:p-3',
          'ea-product-card__content',
        )}
      >
        {image?.url && (
          <Link prefetchOnHover href={productUrl ?? '#'} className="" prefetch={false}>
            <Image
              alt={resolveImageAlt(image, product?.name)}
              src={image?.url ?? '/images/placeholder.png'}
              width="0"
              height="0"
              sizes={buildImageSizes([{ size: 'auto' }, { size: '400px' }])}
              className="aspect-square w-full rounded-lg object-contain"
            />
          </Link>
        )}

        <Link
          prefetchOnHover
          href={productUrl ?? '#'}
          className={cn('text-center text-lg font-bold', 'ea-product-card__name')}
          prefetch={false}
        >
          {product.name}
        </Link>

        {!!templateSettings.product_teaser?.short_description?.enabled && (
          <HtmlRenderer
            className={cn(
              'line-clamp-4 max-h-[90px] overflow-hidden text-ellipsis text-center !text-sm font-normal text-muted-foreground',
              'ea-product-card__short-description',
            )}
            forcedTextSize="text-sm"
            html={product.short_description}
          />
        )}
      </CardContent>

      {/* footer */}
      <CardFooter
        className={cn(
          'flex flex-col justify-end gap-2 p-1 pt-0 @container @[200px]:p-3',
          'ea-product-card__footer',
        )}
      >
        <div
          className={cn(
            'flex w-full flex-row items-center justify-between',
            'ea-product-card__footer-top',
          )}
        >
          {/* {product.video && (
            <Button
              variant="link"
              className={cn(
                'flex h-auto flex-row items-center space-x-2 px-0 py-0 text-destructive underline',
                'ea-product-card__video-button',
              )}
              // onClick={() => handleVideoClick(product.video ?? '')}
            >
              <FaYoutube className={cn('h-4 w-4', 'ea-product-card__video-icon')} />
              <span
                className={cn(
                  'text-xs font-medium text-destructive',
                  'ea-product-card__video-text',
                )}
              >
                Video
              </span>
            </Button>
          )} */}
        </div>

        {/* Price */}
        {/* <Price product={product} filterVariants={filterVariants} /> */}
        {!ASK_FOR_PRICE && (
          <ProductPrice
            product={product}
            className={{
              main: cn('items-center', 'ea-product-card__product-price'),
              discountWrapper: 'hidden',
              priceAfterDiscount: 'text-[18px]',
            }}
          />
        )}

        {ASK_FOR_PRICE && (
          <div className={cn('ea-product-card__ask-for-price')}>
            <button
              type="button"
              className="text-sm font-semibold text-primary underline-offset-4 hover:underline"
              onClick={() => setIsOpenAskForPriceDialog(true)}
            >
              {t('text2278')}
            </button>
            <AskForPriceDialog
              isOpen={isOpenAskForPriceDialog}
              setIsOpen={setIsOpenAskForPriceDialog}
              product={product}
            />
          </div>
        )}

        <div className="flex w-full gap-2">
          {!!templateSettings.product_teaser?.cart_button?.enabled && !ASK_FOR_PRICE && (
            <AddToCartButton
              product={product}
              showIcon={!!templateSettings.product_teaser?.cart_icon}
              sellable={isSellable(product, 1)}
              quantity={1}
              className={cn('flex-grow', 'ea-product-card__add-to-cart-button')}
              // variant="accent"
            />
          )}

          {!!templateSettings.product_teaser?.cart_button?.enabled && ASK_FOR_PRICE && (
            <Button asChild className={cn('w-full', 'ea-product-card__detail-button')}>
              <Link prefetchOnHover href={productUrl ?? '#'}>
                {t('text533')}
              </Link>
            </Button>
          )}
          {!!templateSettings.product_teaser?.favorite_icon?.enabled && (
            <div className={cn('hidden @[200px]:block', 'ea-product-card__add-to-favorites')}>
              <AddToFavoritesButton
                slug={product.slug}
                isFavorite={product.is_favorite ?? false}
                productId={product.id}
              />
            </div>
          )}
        </div>

        {!!templateSettings.product_teaser?.detail_button?.enabled && (
          <Button
            asChild
            variant="secondary"
            className={cn('w-full', 'ea-product-card__detail-button')}
          >
            <Link prefetchOnHover href={productUrl ?? '#'}>
              {t('text533')}
            </Link>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
